import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import {
  fetchBatcheProducts,
  newbatchProducts,
} from '../../../containers/actions/common/saslAction';
import { useStyles } from './Styles';

// Same control and data source as the Batch Execution dashboard's product
// filter (sasl/batchesDetails/batchFilter/SearchSelect.jsx), minus its
// batchFilters() dispatch — that slice is the batch dashboard's own filter
// persistence, and writing to it here would leak this selection onto that screen.
const TYPING_DEBOUNCE_MS = 1000;

const ProductSearchSelect = ({ value, onChange, vendorIDs }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Joined, because vendorIDs is a new array each parent render.
  const vendorKey = (vendorIDs || []).filter((id) => id != null).join(',');

  useEffect(() => {
    if (typed === '') return undefined;
    dispatch(
      fetchBatcheProducts({
        searchProduct: typed.trim(),
        vendorMosymphonyIDList: vendorKey
          ? vendorKey.split(',').map((id) => Number(id))
          : [],
        aslNumberList: [],
      })
    );
    return () => {
      if (newbatchProducts) {
        newbatchProducts.cancel('PO alerts product filter changed');
      }
    };
  }, [dispatch, typed, vendorKey]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const productSlice = useSelector(
    (state) => state?.pcmp?.common?.sasl?.fetchBatchProducts
  );

  // Any settled response stops the spinner, so a failure falls through to
  // "no match found" rather than spinning forever.
  useEffect(() => {
    setLoading(false);
  }, [productSlice]);

  const products =
    productSlice?.status === true && Array.isArray(productSlice.data)
      ? productSlice.data
      : [];

  const isCMO = localStorage.getItem('collabType') === 'CMO';
  const codeOf = (option) =>
    (isCMO ? option?.cmoFGMaterialCode : option?.fgMaterialCode) || '';
  const nameOf = (option) =>
    (isCMO ? option?.cmoFGMaterialName : option?.fgMaterialName) || '';

  const labelOf = (option) => {
    const code = codeOf(option);
    const name = nameOf(option);
    if (code && name) return `${code} / ${name}`;
    return code || name || '';
  };

  // The endpoint is a search, not a catalogue, so nothing is offered until
  // something is typed.
  const options =
    typed === '' ? [] : products.filter((option) => codeOf(option) || nameOf(option));

  const debouncedSetTyped = useCallback((next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setTyped(next), TYPING_DEBOUNCE_MS);
  }, []);

  const handleInputChange = (event) => {
    const next = event.target.value;
    if (next === '') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setTyped('');
      setLoading(false);
      return;
    }
    if (!loading) setLoading(true);
    debouncedSetTyped(next);
  };

  return (
    <Autocomplete
      id="po-alerts-product-filter"
      className={classes.productSelect}
      options={options}
      value={value}
      onChange={(event, selected) => onChange(selected)}
      getOptionLabel={labelOf}
      getOptionSelected={(option, selected) => codeOf(option) === codeOf(selected)}
      clearOnBlur={false}
      loading={loading}
      loadingText="Loading…"
      noOptionsText={
        typed
          ? 'No match found. Please try a different search.'
          : 'Please enter a material code / description'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Search using Material Code / Description"
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon fontSize="small" />,
          }}
          onChange={handleInputChange}
        />
      )}
    />
  );
};

ProductSearchSelect.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  vendorIDs: PropTypes.array,
};

ProductSearchSelect.defaultProps = {
  value: null,
  vendorIDs: [],
};

export default ProductSearchSelect;
